import React, { useState, useCallback } from 'react';
import '../styles/AbaPayRuns.css';
import { FiDownload, FiCheckCircle, FiXCircle, FiRefreshCw, FiFileText, FiEye } from 'react-icons/fi';
import Cookies from 'js-cookie';
import CommonLoader from '../../../components/common/CommonLoader';
import STPSubmissionPanel from './STPSubmissionPanel';
import STPStatusBadge from './STPStatusBadge';
import { GRAPHQL_HTTP_URL } from '../../../config/api';

const GQL = GRAPHQL_HTTP_URL;

// ── GraphQL ────────────────────────────────────────────────────────────────

const ABA_PAY_RUNS_QUERY = `
  query {
    abaPayRuns {
      id
      payPeriodStart
      payPeriodEnd
      description
      remitterName
      bankAbbreviation
      apcaId
      totalCreditAmountCents
      employeeCount
      status
      generatedAt
      approvedAt
      releasedAt
      payrunLocked
      voidReason
    }
  }
`;

const STP_STATUS_QUERY = `
  query GetPayrollSTPStatus($payrollId: String!) {
    getPayrollSTPStatus(payrollId: $payrollId) {
      payrollId
      stpStatus
      latestStatus
      lastSubmissionDate
      submissionType
      errorMessage
    }
  }
`;

const APPROVE_MUTATION = `
  mutation ApproveAba($id: String!, $comment: String) {
    approveAbaPayRun(id: $id, comment: $comment) {
      id
      status
      approvedAt
    }
  }
`;

const RELEASE_MUTATION = `
  mutation ReleaseAba($id: String!, $comment: String) {
    releaseAbaPayRun(id: $id, comment: $comment) {
      payRun { id status releasedAt }
      abaFileContent
    }
  }
`;

const VOID_MUTATION = `
  mutation VoidAba($id: String!, $reason: String!) {
    voidAbaPayRun(id: $id, reason: $reason) {
      id
      status
      voidReason
    }
  }
`;

// ── Helpers ────────────────────────────────────────────────────────────────

const gql = async (query, variables = {}) => {
    const token = Cookies.get('accessToken');
    const res = await fetch(GQL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors?.length) throw new Error(json.errors[0].message);
    return json.data;
};

const fmt = (cents) =>
    `$${(Number(cents ?? 0) / 100).toLocaleString('en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = String(d).split('T')[0].split('-');
    return `${day}/${m}/${y}`;
};

const fmtDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const STATUS_META = {
    // Align display labels with business wording without changing internal enums.
    PENDING_APPROVAL: { label: 'Generated (Pending Approval)', cls: 'aba-badge--pending' },
    APPROVED:         { label: 'Approved',          cls: 'aba-badge--approved' },
    RELEASED:         { label: 'Downloaded / Released', cls: 'aba-badge--released' },
    VOIDED:           { label: 'Voided',             cls: 'aba-badge--voided' },
    DRAFT:            { label: 'Draft',              cls: 'aba-badge--draft' },
};

// ── Download helper ────────────────────────────────────────────────────────

const downloadAba = (content, periodStart, periodEnd) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    const name = `payroll_${String(periodStart).split('T')[0]}_${String(periodEnd).split('T')[0]}.aba`;
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ── Component ──────────────────────────────────────────────────────────────

export default function AbaPayRuns() {
    const [runs, setRuns]           = useState(null);   // null = not loaded yet
    const [loading, setLoading]     = useState(false);
    const [actionId, setActionId]   = useState(null);   // which row is processing
    const [error, setError]         = useState('');
    const [stpStatusMap, setStpStatusMap] = useState({}); // key=abaPayRunId -> PayrollSTPStatus
    const [detailsRun, setDetailsRun] = useState(null);   // selected ABA run for details

    // Void modal
    const [voidModal, setVoidModal] = useState(null);   // { id, status, stpStatus }
    const [voidReason, setVoidReason] = useState('');

    // ── Load ──────────────────────────────────────────────────────────────
    const loadRuns = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await gql(ABA_PAY_RUNS_QUERY);
            // Show ALL ABA pay runs returned by the API.
            // Filtering by payrunLocked can accidentally hide active runs (e.g. a PENDING_APPROVAL
            // run with payrunLocked=false due to legacy data or a prior flow), which then prevents
            // users from approving/voiding the run that is blocking payroll.
            const allRuns = (data.abaPayRuns ?? []);
            setRuns(allRuns);

            // Load STP status for each run (by abaPayRunId)
            const statuses = await Promise.all(
              allRuns.map(async (r) => {
                try {
                  const s = await gql(STP_STATUS_QUERY, { payrollId: r.id });
                  return [r.id, s.getPayrollSTPStatus];
                } catch {
                  return [r.id, null];
                }
              }),
            );
            const map = {};
            for (const [id, s] of statuses) {
              if (s) map[id] = s;
            }
            setStpStatusMap(map);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load on first render
    React.useEffect(() => { loadRuns(); }, [loadRuns]);

    // ── Approve ───────────────────────────────────────────────────────────
    const handleApprove = async (id) => {
        setActionId(id);
        setError('');
        try {
            await gql(APPROVE_MUTATION, { id });
            await loadRuns();
        } catch (e) {
            setError(e.message);
        } finally {
            setActionId(null);
        }
    };

    // ── Release + Download ────────────────────────────────────────────────
    const handleRelease = async (run) => {
        setActionId(run.id);
        setError('');
        try {
            const data = await gql(RELEASE_MUTATION, { id: run.id });
            const { abaFileContent } = data.releaseAbaPayRun;
            downloadAba(abaFileContent, run.payPeriodStart, run.payPeriodEnd);
            await loadRuns();
        } catch (e) {
            setError(e.message);
        } finally {
            setActionId(null);
        }
    };

    // ── Void ──────────────────────────────────────────────────────────────
    const handleVoidSubmit = async () => {
        if (!voidReason.trim()) return;
        const id = voidModal.id;
        setVoidModal(null);
        setActionId(id);
        setError('');
        try {
            await gql(VOID_MUTATION, { id, reason: voidReason.trim() });
            setVoidReason('');
            await loadRuns();
        } catch (e) {
            setError(e.message);
        } finally {
            setActionId(null);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="aba-page">

            {/* Header */}
            <div className="aba-header">
                <div>
                    <h2 className="aba-title">ABA Pay Runs</h2>
                    <p className="aba-subtitle">
                        Review, approve, and download ABA Direct Entry payment files
                    </p>
                </div>
                <button className="aba-btn aba-btn--refresh" onClick={loadRuns} disabled={loading}>
                    <FiRefreshCw className={loading ? 'aba-spin' : ''} />
                    Refresh
                </button>
            </div>

            {/* Error banner */}
            {error && <div className="aba-error">{error}</div>}

            {/* Loading */}
            {loading && runs === null && (
                <div className="aba-loader"><CommonLoader /></div>
            )}

            {/* Empty state */}
            {!loading && runs?.length === 0 && (
                <div className="aba-empty">
                    <FiFileText size={40} className="aba-empty-icon" />
                    <p>No ABA pay runs yet.</p>
                    <p className="aba-empty-hint">
                        Run payroll first — an ABA file is created automatically after each pay run.
                    </p>
                </div>
            )}

            {/* Table */}
            {runs?.length > 0 && (
                <div className="aba-card">
                    <table className="aba-table">
                        <thead>
                            <tr>
                                <th>Period</th>
                                <th>Description</th>
                                <th>Bank</th>
                                <th>Employees</th>
                                <th>Total</th>
                                <th>Generated</th>
                                <th>Status</th>
                                <th>STP</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map(run => {
                                const meta    = STATUS_META[run.status] ?? STATUS_META.DRAFT;
                                const busy    = actionId === run.id;
                                const isPend  = run.status === 'PENDING_APPROVAL';
                                const isApprv = run.status === 'APPROVED';
                                const isRel   = run.status === 'RELEASED';
                                const isVoid  = run.status === 'VOIDED';
                                const stpStatus = stpStatusMap[run.id]?.stpStatus ?? 'NOT_SUBMITTED';
                                const stpLatest = (stpStatusMap[run.id]?.latestStatus ?? '').toString().toUpperCase();
                                const isVoidBlocked = isRel || stpLatest === 'SUCCESS';

                                return (
                                    <tr key={run.id}>
                                        <td className="aba-period">
                                            {fmtDate(run.payPeriodStart)} – {fmtDate(run.payPeriodEnd)}
                                        </td>
                                        <td>{run.description}</td>
                                        <td>
                                            <span className="aba-bank">
                                                {run.bankAbbreviation}
                                            </span>
                                        </td>
                                        <td className="aba-center">{run.employeeCount}</td>
                                        <td className="aba-amount">{fmt(run.totalCreditAmountCents)}</td>
                                        <td className="aba-dt">{fmtDateTime(run.generatedAt)}</td>
                                        <td>
                                            <span className={`aba-badge ${meta.cls}`}>
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td>
                                            <STPStatusBadge status={stpStatus} />
                                        </td>
                                        <td>
                                            <div className="aba-actions">
                                                <button
                                                    className="aba-btn aba-btn--release"
                                                    onClick={() => setDetailsRun(run)}
                                                    disabled={busy}
                                                    title="View payroll details"
                                                >
                                                    <FiEye />
                                                    Details
                                                </button>
                                                {isPend && (
                                                    <button
                                                        className="aba-btn aba-btn--approve"
                                                        onClick={() => handleApprove(run.id)}
                                                        disabled={busy}
                                                    >
                                                        <FiCheckCircle />
                                                        {busy ? '…' : 'Approve'}
                                                    </button>
                                                )}
                                                {isApprv && (
                                                    <button
                                                        className="aba-btn aba-btn--release"
                                                        onClick={() => handleRelease(run)}
                                                        disabled={busy}
                                                    >
                                                        <FiDownload />
                                                        {busy ? 'Downloading…' : 'Release & Download'}
                                                    </button>
                                                )}
                                                {isRel && (
                                                    <button
                                                        className="aba-btn aba-btn--release"
                                                        onClick={() => handleRelease(run)}
                                                        disabled={busy}
                                                    >
                                                        <FiDownload />
                                                        {busy ? 'Downloading…' : 'Download again'}
                                                    </button>
                                                )}
                                                {!isVoid && !isVoidBlocked && (
                                                    <button
                                                        className="aba-btn aba-btn--void"
                                                        onClick={() => { setVoidModal({ id: run.id, status: run.status, stpStatus: stpStatusMap[run.id]?.stpStatus ?? 'NOT_SUBMITTED' }); setVoidReason(''); }}
                                                        disabled={busy}
                                                    >
                                                        <FiXCircle />
                                                        Void
                                                    </button>
                                                )}
                                                {!isVoid && isVoidBlocked && (
                                                    <span
                                                        className="aba-released-tag"
                                                        title={
                                                            isRel
                                                                ? 'Void is blocked because this ABA file has been released/downloaded.'
                                                                : 'Void is blocked because STP has already been submitted for this period.'
                                                        }
                                                    >
                                                        Void blocked
                                                    </span>
                                                )}
                                                {run.status === 'RELEASED' && (
                                                    <span className="aba-released-tag">File sent</span>
                                                )}
                                                {run.status === 'VOIDED' && run.voidReason && (
                                                    <span className="aba-void-reason" title={run.voidReason}>
                                                        {run.voidReason.slice(0, 30)}{run.voidReason.length > 30 ? '…' : ''}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Details modal */}
            {detailsRun && (
                <div className="aba-modal-overlay" onClick={() => setDetailsRun(null)}>
                    <div className="aba-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 980 }}>
                        <h3 className="aba-modal-title">Payroll Details</h3>
                        <p className="aba-modal-desc">
                            Period: <strong>{fmtDate(detailsRun.payPeriodStart)} – {fmtDate(detailsRun.payPeriodEnd)}</strong>
                        </p>

                        <STPSubmissionPanel
                            payrollId={detailsRun.id}
                            payrollFinalized={detailsRun.payrunLocked === true && detailsRun.status !== 'VOIDED'}
                        />

                        <div className="aba-modal-footer">
                            <button className="aba-btn aba-btn--cancel" onClick={() => setDetailsRun(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Void modal */}
            {voidModal && (
                <div className="aba-modal-overlay" onClick={() => setVoidModal(null)}>
                    <div className="aba-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="aba-modal-title">Void ABA Pay Run</h3>

                        {/* RELEASED warning — file already sent to bank */}
                        {voidModal?.status === 'RELEASED' && (
                            <p className="aba-modal-desc" style={{ color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                                <strong>ABA file already downloaded.</strong> Voiding here does <strong>not</strong> reverse the bank payment — contact your bank separately to cancel the transfer.
                                {' '}<strong>Note: payroll re-run for this period will be blocked</strong> because the file was already sent to the bank.
                            </p>
                        )}

                        {/* STP submitted warning — re-run will be blocked */}
                        {voidModal?.stpStatus === 'SUBMITTED' && (
                            <p className="aba-modal-desc" style={{ color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                                <strong>STP already submitted to the ATO.</strong> Voiding here does <strong>not</strong> cancel the ATO submission.
                                {' '}<strong>Note: payroll re-run for this period will be blocked</strong> because an STP PAYEVENT was already accepted by the ATO. Use the STP panel to submit an UPDATEEVENT for corrections.
                            </p>
                        )}

                        {/* Safe void — neither downloaded nor STP submitted */}
                        {voidModal?.status !== 'RELEASED' && voidModal?.stpStatus !== 'SUBMITTED' && (
                            <p className="aba-modal-desc">
                                Voiding will unlock payroll for this period so it can be re-run. Provide a reason:
                            </p>
                        )}
                        {(voidModal?.status === 'RELEASED' || voidModal?.stpStatus === 'SUBMITTED') && (
                            <p className="aba-modal-desc">
                                Provide a reason for voiding:
                            </p>
                        )}
                        <textarea
                            className="aba-modal-textarea"
                            placeholder="e.g. Bank details incorrect, regenerating..."
                            value={voidReason}
                            onChange={e => setVoidReason(e.target.value)}
                            rows={3}
                            autoFocus
                        />
                        <div className="aba-modal-footer">
                            <button className="aba-btn aba-btn--cancel" onClick={() => setVoidModal(null)}>
                                Cancel
                            </button>
                            <button
                                className="aba-btn aba-btn--void"
                                onClick={handleVoidSubmit}
                                disabled={!voidReason.trim()}
                            >
                                Confirm Void
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
